import { CheckCircle2, Loader2, MessageSquare, ChevronDown } from 'lucide-react';
import { useEffect, type ReactNode, useRef, useState, type FormEvent } from 'react';
import { getContactSubmitErrorMessage, isValidEmail, normalizeText } from '../public/contactFormShared';
import useContactTurnstile from '../public/useContactTurnstile';

type SupportTopic =
  | 'Import trades'
  | 'Broker connection'
  | 'Billing'
  | 'Account/login'
  | 'Bug report'
  | 'Feature request'
  | 'Other';

type SupportFormValues = {
  name: string;
  email: string;
  topic: SupportTopic;
  message: string;
};

type SupportFieldErrors = Partial<Record<keyof SupportFormValues, string>>;

type SupportAccountContext = {
  uid?: string | null;
  email?: string | null;
};

type SupportRequestContext = {
  currentPage: string;
  authenticatedUserId: string;
  authenticatedUserEmail: string;
};

type SupportContactResponse = {
  ok: boolean;
  referenceId?: string | null;
  destinationTeamLabel?: string;
  submitterEmail?: string;
  routedToLabel?: string;
  deliveryWarning?: 'confirmation_email_failed' | string;
  error?: string;
  fieldErrors?: Partial<Record<'name' | 'email' | 'topic' | 'message' | 'subject' | 'inquiryType', string>>;
};

const CONTACT_SUBMIT_TIMEOUT_MS = 20_000;

type SupportSubmissionResult = {
  response: Response;
  body: SupportContactResponse | null;
};

const SUPPORT_TOPICS: SupportTopic[] = [
  'Import trades',
  'Broker connection',
  'Billing',
  'Account/login',
  'Bug report',
  'Feature request',
  'Other',
];

function createInitialForm(): SupportFormValues {
  return {
    name: '',
    email: '',
    topic: 'Import trades',
    message: '',
  };
}

function buildSubject(topic: SupportTopic) {
  return `Support request: ${topic}`;
}

function validateForm(form: SupportFormValues): SupportFieldErrors {
  const errors: SupportFieldErrors = {};

  if (!normalizeText(form.name)) errors.name = 'Name is required.';
  if (!normalizeText(form.email)) errors.email = 'Email is required.';
  else if (!isValidEmail(form.email)) errors.email = 'Enter a valid email address.';
  if (!SUPPORT_TOPICS.includes(form.topic)) errors.topic = 'Choose a topic.';
  if (!normalizeText(form.message)) errors.message = 'Message is required.';

  return errors;
}

function createSupportSubmitTimeoutError() {
  const error = new Error('The support request took too long to send. Please try again.');
  error.name = 'SupportRequestTimeoutError';
  return error;
}

async function submitSupportRequest(
  form: SupportFormValues,
  turnstileToken: string,
  context: SupportRequestContext,
  signal?: AbortSignal,
): Promise<SupportSubmissionResult> {
  const response = await fetch('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal,
    body: JSON.stringify({
      name: normalizeText(form.name),
      email: normalizeText(form.email),
      inquiryType: 'Product Support',
      subject: buildSubject(form.topic),
      topic: form.topic,
      message: normalizeText(form.message),
      currentPage: normalizeText(context.currentPage),
      authenticatedUserId: normalizeText(context.authenticatedUserId),
      authenticatedUserEmail: normalizeText(context.authenticatedUserEmail),
      source: 'support_modal',
      companyWebsite: '',
      turnstileToken,
    }),
  });

  const body = (await response.json().catch(() => null)) as SupportContactResponse | null;
  return { response, body };
}

async function submitSupportRequestWithTimeout(
  form: SupportFormValues,
  turnstileToken: string,
  context: SupportRequestContext,
): Promise<SupportSubmissionResult> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      controller?.abort();
      reject(createSupportSubmitTimeoutError());
    }, CONTACT_SUBMIT_TIMEOUT_MS);
  });

  try {
    return await Promise.race([
      submitSupportRequest(form, turnstileToken, context, controller?.signal),
      timeoutPromise,
    ]);
  } finally {
    if (typeof timeoutId !== 'undefined') {
      clearTimeout(timeoutId);
    }
  }
}

function SupportSuccessState({
  topic,
  referenceId,
  teamLabel,
  submitterEmail,
  deliveryNotice,
  onReset,
}: {
  topic: SupportTopic;
  referenceId: string;
  teamLabel: string;
  submitterEmail: string;
  deliveryNotice: string;
  onReset: () => void;
}) {
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm dark:border-emerald-500/20 dark:bg-emerald-500/10">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white dark:bg-emerald-500">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-emerald-950 dark:text-emerald-50">Message sent</h3>
          <p className="mt-1 text-sm leading-6 text-emerald-900/80 dark:text-emerald-50/80">
            Your {topic} request was routed to the {teamLabel || 'Support'} team.
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3 shadow-sm dark:border-emerald-500/20 dark:bg-zinc-950/60">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Reference ID</div>
          <div className="mt-1 font-mono text-sm font-semibold text-zinc-900 dark:text-zinc-100">{referenceId || '—'}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3 shadow-sm dark:border-emerald-500/20 dark:bg-zinc-950/60">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Confirmation email</div>
          <div className="mt-1 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{submitterEmail || '—'}</div>
        </div>
      </div>

      {deliveryNotice ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
          {deliveryNotice}
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
        >
          Send another message
        </button>
      </div>
    </div>
  );
}

type SupportContactSectionProps = {
  titleId?: string;
  headerAction?: ReactNode;
  className?: string;
  accountContext?: SupportAccountContext | null;
};

export default function SupportContactSection({ titleId, headerAction, className = '', accountContext = null }: SupportContactSectionProps) {
  const [form, setForm] = useState<SupportFormValues>(() => createInitialForm());
  const [fieldErrors, setFieldErrors] = useState<SupportFieldErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [referenceId, setReferenceId] = useState('');
  const [teamLabel, setTeamLabel] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [deliveryNotice, setDeliveryNotice] = useState('');
  const [isDark, setIsDark] = useState<boolean>(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  const isSubmittingRef = useRef(false);

  const {
    resetTurnstileWidget,
    turnstileError,
    turnstileHostRef,
    turnstileSiteKey,
    turnstileStatus,
    turnstileToken,
  } = useContactTurnstile(isDark ? 'dark' : 'light');

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const update = () => setIsDark(document.documentElement.classList.contains('dark'));
    const observer = new MutationObserver(() => update());

    try {
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    } catch {
      // ignore non-browser or unsupported environments
    }

    update();

    return () => {
      try {
        observer.disconnect();
      } catch {
        // ignore cleanup issues
      }
    };
  }, []);

  function updateField<K extends keyof SupportFormValues>(field: K, value: SupportFormValues[K]) {
    setForm((previous) => ({ ...previous, [field]: value }));
    setFieldErrors((previous) => ({ ...previous, [field]: '' }));
    setSubmitError('');
  }

  function resetForm() {
    setForm(createInitialForm());
    setFieldErrors({});
    setSubmitError('');
    setStatus('idle');
    setReferenceId('');
    setTeamLabel('');
    setSubmitterEmail('');
    setDeliveryNotice('');
    resetTurnstileWidget();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'submitting' || isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const nextErrors = validateForm(form);
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSubmitError('');
      isSubmittingRef.current = false;
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setSubmitError('Please complete the security check before sending.');
      isSubmittingRef.current = false;
      return;
    }

    setStatus('submitting');
    setSubmitError('');

    try {
      const { response, body } = await submitSupportRequestWithTimeout(form, turnstileSiteKey ? turnstileToken : '', {
        currentPage: typeof window !== 'undefined' ? window.location.href : '',
        authenticatedUserId: accountContext?.uid ? String(accountContext.uid) : '',
        authenticatedUserEmail: accountContext?.email ? String(accountContext.email) : '',
      });

      if (!response.ok || !body?.ok) {
        resetTurnstileWidget();
        if (body?.fieldErrors) {
          setFieldErrors((previous) => ({
            ...previous,
            name: body.fieldErrors?.name ?? previous.name ?? '',
            email: body.fieldErrors?.email ?? previous.email ?? '',
            topic: body.fieldErrors?.topic ?? body.fieldErrors?.inquiryType ?? previous.topic ?? '',
            message: body.fieldErrors?.message ?? previous.message ?? '',
          }));
        }
        setSubmitError(getContactSubmitErrorMessage(body?.error));
        setStatus('idle');
        return;
      }

      setReferenceId(body.referenceId || '');
      setTeamLabel(body.destinationTeamLabel || body.routedToLabel || 'Support');
      setSubmitterEmail(body.submitterEmail || form.email.trim());
      setDeliveryNotice(
        body.deliveryWarning === 'confirmation_email_failed'
          ? 'Your message was sent, but the confirmation email could not be delivered. Our team still received your request.'
          : ''
      );
      setStatus('success');
      resetTurnstileWidget();
    } catch (error) {
      resetTurnstileWidget();
      if (error instanceof Error && error.name === 'SupportRequestTimeoutError') {
        setSubmitError(createSupportSubmitTimeoutError().message);
      } else {
        setSubmitError('We could not send your message right now. Please try again in a moment.');
      }
      setStatus('idle');
    } finally {
      isSubmittingRef.current = false;
    }
  }

  return (
    <section
      id="contact-support"
      className={`flex scroll-mt-24 flex-col rounded-2xl border border-border bg-white p-5 shadow-sm transition-all dark:bg-zinc-900 sm:p-6 ${className}`.trim()}
    >
      <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
            <MessageSquare className="h-4 w-4" />
          </div>
          <div>
            <h2 id={titleId} className="text-xl font-bold tracking-tight text-zinc-950 dark:text-white">Contact Support</h2>
            <p className="mt-1 text-sm leading-6 text-zinc-500 dark:text-zinc-400">
              Reach out about import trades, broker connection, billing, account access, bugs, or feature requests.
            </p>
          </div>
        </div>
        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </div>

      <div className="mt-5 flex flex-1 flex-col">
        {status === 'success' ? (
          <SupportSuccessState
            topic={form.topic}
            referenceId={referenceId}
            teamLabel={teamLabel}
            submitterEmail={submitterEmail}
            deliveryNotice={deliveryNotice}
            onReset={resetForm}
          />
        ) : (
          <form className="flex flex-1 flex-col gap-5" onSubmit={handleSubmit} noValidate aria-busy={status === 'submitting'}>
            {submitError ? (
              <div className="rounded-xl border border-pink-200 bg-pink-50 px-4 py-3 text-sm leading-6 text-pink-800 dark:border-pink-500/20 dark:bg-pink-500/10 dark:text-pink-100">
                {submitError}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400" htmlFor="support-name">
                  Name
                </label>
                <input
                  id="support-name"
                  type="text"
                  value={form.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  autoComplete="name"
                  className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-zinc-900 shadow-sm outline-none transition placeholder:text-zinc-400 focus:ring-2 dark:bg-zinc-950/60 dark:text-zinc-100 dark:placeholder:text-zinc-600 ${
                    fieldErrors.name
                      ? 'border-pink-300 focus:border-pink-400 focus:ring-pink-300/20 dark:border-pink-500/40'
                      : 'border-zinc-200 focus:border-blue-500/60 focus:ring-blue-500/10 dark:border-zinc-800'
                  }`}
                  placeholder="Full name"
                />
                {fieldErrors.name ? <p className="text-xs font-medium text-pink-600 dark:text-pink-300">{fieldErrors.name}</p> : null}
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400" htmlFor="support-email">
                  Email
                </label>
                <input
                  id="support-email"
                  type="email"
                  value={form.email}
                  onChange={(event) => updateField('email', event.target.value)}
                  autoComplete="email"
                  className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-zinc-900 shadow-sm outline-none transition placeholder:text-zinc-400 focus:ring-2 dark:bg-zinc-950/60 dark:text-zinc-100 dark:placeholder:text-zinc-600 ${
                    fieldErrors.email
                      ? 'border-pink-300 focus:border-pink-400 focus:ring-pink-300/20 dark:border-pink-500/40'
                      : 'border-zinc-200 focus:border-blue-500/60 focus:ring-blue-500/10 dark:border-zinc-800'
                  }`}
                  placeholder="email@address.com"
                />
                {fieldErrors.email ? <p className="text-xs font-medium text-pink-600 dark:text-pink-300">{fieldErrors.email}</p> : null}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400" htmlFor="support-topic">
                Topic
              </label>
              <div className="relative">
                <select
                  id="support-topic"
                  value={form.topic}
                  onChange={(event) => updateField('topic', event.target.value as SupportTopic)}
                  className={`h-12 w-full appearance-none rounded-xl border bg-white px-4 pr-11 text-sm text-zinc-900 shadow-sm outline-none transition focus:ring-2 dark:bg-zinc-950/60 dark:text-zinc-100 ${
                    fieldErrors.topic
                      ? 'border-pink-300 focus:border-pink-400 focus:ring-pink-300/20 dark:border-pink-500/40'
                      : 'border-zinc-200 focus:border-blue-500/60 focus:ring-blue-500/10 dark:border-zinc-800'
                  }`}
                >
                  {SUPPORT_TOPICS.map((topic) => (
                    <option key={topic} value={topic} className="bg-zinc-900 text-zinc-100">
                      {topic}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
              </div>
              {fieldErrors.topic ? <p className="text-xs font-medium text-pink-600 dark:text-pink-300">{fieldErrors.topic}</p> : null}
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400" htmlFor="support-message">
                Message
              </label>
              <textarea
                id="support-message"
                rows={9}
                value={form.message}
                onChange={(event) => updateField('message', event.target.value)}
                className={`min-h-[190px] w-full resize-none rounded-xl border bg-white px-4 py-3 text-sm text-zinc-900 shadow-sm outline-none transition placeholder:text-zinc-400 focus:ring-2 dark:bg-zinc-950/60 dark:text-zinc-100 dark:placeholder:text-zinc-600 ${
                  fieldErrors.message
                    ? 'border-pink-300 focus:border-pink-400 focus:ring-pink-300/20 dark:border-pink-500/40'
                    : 'border-zinc-200 focus:border-blue-500/60 focus:ring-blue-500/10 dark:border-zinc-800'
                }`}
                placeholder="Tell us what happened and what you expected."
              />
              {fieldErrors.message ? <p className="text-xs font-medium text-pink-600 dark:text-pink-300">{fieldErrors.message}</p> : null}
            </div>

            {turnstileSiteKey ? (
              <div className="space-y-2 rounded-2xl border border-border bg-zinc-50 p-4 dark:bg-zinc-950/60">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Security check</p>
                <div ref={turnstileHostRef} />
                {turnstileStatus === 'loading' ? (
                  <p className="text-xs leading-6 text-zinc-500 dark:text-zinc-400">Loading the security check...</p>
                ) : null}
                {turnstileStatus === 'error' ? (
                  <p className="text-xs leading-6 text-pink-600 dark:text-pink-300">{turnstileError || 'The security check could not load. Refresh the page and try again.'}</p>
                ) : null}
              </div>
            ) : null}

            <div className="mt-auto flex flex-col gap-3 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-6 text-zinc-500 dark:text-zinc-400">
                We’ll route your request to the right inbox and reply as soon as we can.
              </p>
              <button
                type="submit"
                disabled={status === 'submitting' || (turnstileSiteKey ? !turnstileToken : false)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === 'submitting' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {status === 'submitting' ? 'Sending...' : 'Send message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
